import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

/**
 * Leichtgewichtige, eigene Tour-Engine (ohne Fremdbibliothek):
 * - startet automatisch nur einmal pro Benutzer (localStorage pro User-ID)
 * - überspringbar, Fortschritt wird gespeichert (Resume nach Reload)
 * - funktioniert über Seitenwechsel hinweg (navigiert selbst zur Ziel-Route)
 * - zeigt auf echte UI-Elemente via [data-tour]-Anker
 * - Desktop: Popover am Element · Mobile: dezentes Bottom-Sheet
 */

export const TOUR_STEPS = [
  {
    route: "/app",
    target: '[data-tour="dashboard-cta"]',
    title: "Willkommen bei CarOS",
    body: "Das ist Ihr Startpunkt: Mit einem Klick auf „Neues Fahrzeug veröffentlichen“ beginnt der komplette Ablauf – vom Fahrzeug zum fertigen Inserat in wenigen Minuten.",
  },
  {
    route: "/app",
    target: '[data-tour="dashboard-steps"]',
    title: "Drei Schritte zum Inserat",
    body: "1. Fahrzeugdaten laden, 2. Bilder mit der Fotobooth optimieren, 3. Inserat erstellen und veröffentlichen. Der Assistent führt Sie durch jeden Schritt.",
  },
  {
    route: "/app/aufnehmen",
    target: '[data-tour="wizard-vin"]',
    title: "FIN eingeben, scannen oder Demo laden",
    body: "Tippen Sie die Fahrgestellnummer ein oder fotografieren Sie einfach den Fahrzeugschein (Feld E) – die KI liest die FIN automatisch. Zum Ausprobieren laden Sie das Demo-Fahrzeug.",
  },
  {
    route: "/app/aufnehmen",
    target: '[data-tour="wizard-phase-0"]',
    title: "Fahrzeugdaten prüfen",
    body: "Nach dem Laden erscheinen alle automatisch erkannten Daten – Marke, Modell, Motorisierung und Ausstattung. Kurz prüfen und mit einem Klick übernehmen.",
  },
  {
    route: "/app/aufnehmen",
    target: '[data-tour="wizard-phase-1"]',
    title: "Fotos aufnehmen oder hochladen",
    body: "Direkt mit dem Smartphone fotografieren oder vorhandene Bilder hochladen – egal, wo das Fahrzeug gerade steht. Die Foto-Slots führen Sie durch alle Perspektiven.",
  },
  {
    route: "/app/fotobooth",
    target: '[data-tour="fotobooth-hero"]',
    title: "Fotobooth: Studio-Look per KI",
    body: "Hintergrund wählen, Händlerlogo als Wasserzeichen einfügen und Kennzeichen automatisch unkenntlich machen – für einzelne Fotos oder alle auf einmal. Das Fahrzeug selbst bleibt immer unverändert.",
  },
  {
    route: "/app/aufnehmen",
    target: '[data-tour="wizard-phase-2"]',
    title: "Preis & Angaben ergänzen",
    body: "Kilometerstand, Verkaufspreis und kurze Hinweise eintragen. Notizen wie „zwei Schlüssel, Kratzer hinten rechts“ strukturiert die KI automatisch.",
  },
  {
    route: "/app/aufnehmen",
    target: '[data-tour="wizard-phase-3"]',
    title: "KI-Inserat erstellen & bearbeiten",
    body: "Die KI schreibt Börsentitel, Beschreibung und Highlights – ausschließlich aus bestätigten Angaben. Jeder Text lässt sich direkt bearbeiten oder im Stil anpassen.",
  },
  {
    route: "/app/aufnehmen",
    target: '[data-tour="wizard-phase-4"]',
    title: "Veröffentlichungsprüfung",
    body: "Vor der Veröffentlichung prüft CarOS Vollständigkeit und sensible Angaben und zeigt Ihnen eine Inserat-Vorschau, wie Käufer sie sehen würden.",
  },
  {
    route: "/app/veroeffentlichen",
    target: '[data-tour="publishing-overview"]',
    title: "Plattformen auswählen & veröffentlichen",
    body: "Wählen Sie die Kanäle und veröffentlichen Sie das Inserat direkt aus dem Fahrzeug heraus. Hier behalten Sie den Status aller Fahrzeuge im Blick.",
  },
  {
    route: "/app/content",
    target: '[data-tour="content-generator"]',
    title: "Content Studio",
    body: "Erstellen Sie mit einem Klick Social-Media-Entwürfe für Instagram, TikTok, Facebook und WhatsApp – veröffentlicht wird erst nach Ihrer Freigabe.",
  },
  {
    route: "/app/einstellungen",
    target: '[data-tour="settings-help"]',
    title: "Geschafft!",
    body: "Das war die Tour. Sie können sie hier in den Einstellungen jederzeit neu starten. Viel Erfolg mit Ihrem ersten Fahrzeug!",
  },
];

const TourContext = createContext({ active: false, startTour: () => {}, stopTour: () => {} });
export const useTour = () => useContext(TourContext);

const keyFor = (userId) => `caros_tour_v1_${userId || "anon"}`;
const readRec = (userId) => {
  try { return JSON.parse(localStorage.getItem(keyFor(userId)) || "null"); } catch { return null; }
};
const writeRec = (userId, value) => {
  try { localStorage.setItem(keyFor(userId), JSON.stringify({ ...value, updatedAt: new Date().toISOString() })); } catch { /* noop */ }
};

const POP_W = 340;
const POP_H_EST = 185;
const GAP = 12;
const PAD = 6;

export const TourProvider = ({ children }) => {
  const { user, tenant } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [idx, setIdx] = useState(null);
  const [rect, setRect] = useState(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);
  const userId = user?.id;

  const active = idx !== null;
  const step = active ? TOUR_STEPS[idx] : null;
  const isLast = active && idx === TOUR_STEPS.length - 1;

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Auto-Start (nur einmal pro Benutzer) bzw. Resume eines laufenden Durchlaufs
  useEffect(() => {
    if (!userId) return;
    const rec = readRec(userId);
    if (rec && rec.status !== "running") return; // abgeschlossen oder übersprungen -> nie wieder automatisch
    const timer = setTimeout(() => {
      const current = readRec(userId);
      if (!current) {
        writeRec(userId, { status: "running", step: 0 });
        setIdx(0);
      } else if (current.status === "running" && Number.isInteger(current.step)) {
        setIdx(Math.min(Math.max(current.step, 0), TOUR_STEPS.length - 1));
      }
    }, 1400);
    return () => clearTimeout(timer);
  }, [userId]);

  // Fortschritt speichern
  useEffect(() => {
    if (active && userId) writeRec(userId, { status: "running", step: idx });
  }, [idx, active, userId]);

  // Bei Bedarf zur Route des aktuellen Schritts navigieren
  useEffect(() => {
    if (!active || !step) return;
    if (location.pathname !== step.route) navigate(step.route);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, active]);

  const endTour = useCallback((status) => {
    setIdx(null);
    setRect(null);
    if (userId) writeRec(userId, { status, step: null });
    if (status === "done") {
      toast.success("Tour abgeschlossen – Sie können sie jederzeit unter Einstellungen neu starten.");
    }
  }, [userId]);

  const next = useCallback(() => {
    if (idx === null) return;
    if (idx >= TOUR_STEPS.length - 1) endTour("done");
    else setIdx(idx + 1);
  }, [idx, endTour]);

  const prev = useCallback(() => {
    if (idx === null || idx === 0) return;
    setIdx(idx - 1);
  }, [idx]);

  const skip = useCallback(() => endTour("skipped"), [endTour]);

  const startTour = useCallback(() => {
    if (userId) writeRec(userId, { status: "running", step: 0 });
    setRect(null);
    setIdx(0);
  }, [userId]);

  // Ziel-Element suchen & Position laufend aktualisieren
  useEffect(() => {
    if (!active || !step) { setRect(null); return; }
    setRect(null);
    let cancelled = false;
    let scrolledOnce = false;
    let timeoutId = null;
    const startedAt = Date.now();

    const tick = () => {
      if (cancelled) return;
      const el = document.querySelector(step.target);
      const visible = el && el.getClientRects().length > 0;
      if (visible) {
        if (!scrolledOnce) {
          scrolledOnce = true;
          try { el.scrollIntoView({ block: "center", behavior: "smooth" }); } catch { /* noop */ }
        }
        const r = el.getBoundingClientRect();
        setRect((old) => {
          if (old && Math.abs(old.top - r.top) < 1 && Math.abs(old.left - r.left) < 1 && Math.abs(old.width - r.width) < 1 && Math.abs(old.height - r.height) < 1) return old;
          return { top: r.top, left: r.left, width: r.width, height: r.height };
        });
      } else if (Date.now() - startedAt > 6000) {
        // Ziel nicht auffindbar -> Schritt robust überspringen statt hängen zu bleiben
        cancelled = true;
        if (idx >= TOUR_STEPS.length - 1) endTour("done");
        else setIdx(idx + 1);
        return;
      }
      timeoutId = setTimeout(tick, 180);
    };
    tick();
    return () => { cancelled = true; clearTimeout(timeoutId); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, active, location.pathname]);

  // Popover-Position berechnen
  let popStyle = null;
  if (active) {
    if (isMobile) {
      popStyle = { left: 12, right: 12, bottom: 12 };
    } else if (rect) {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const fitsBelow = rect.top + rect.height + GAP + POP_H_EST < vh - 12;
      const top = fitsBelow
        ? rect.top + rect.height + GAP + PAD
        : Math.max(12, rect.top - GAP - POP_H_EST - PAD);
      const left = Math.min(Math.max(12, rect.left + rect.width / 2 - POP_W / 2), Math.max(12, vw - POP_W - 12));
      popStyle = { top, left, width: POP_W };
    }
  }

  const overlay = active
    ? createPortal(
        <div data-testid="tour-overlay">
          {/* Klick-Blocker */}
          <div
            className="fixed inset-0 z-[290]"
            style={{ background: rect ? "transparent" : "rgba(4,10,20,0.55)" }}
            onClick={(e) => e.stopPropagation()}
          />
          {/* Spotlight auf das Ziel-Element */}
          {rect && (
            <div
              className="fixed z-[291] rounded-xl border-2 border-primary/70 pointer-events-none transition-all duration-200"
              data-testid="tour-spotlight"
              style={{
                top: rect.top - PAD,
                left: rect.left - PAD,
                width: rect.width + PAD * 2,
                height: rect.height + PAD * 2,
                boxShadow: "0 0 0 9999px rgba(4,10,20,0.72)",
              }}
            />
          )}
          {/* Popover / Bottom-Sheet */}
          {(rect || isMobile) && popStyle && (
            <div
              className="fixed z-[292] rounded-2xl border border-border bg-card/95 backdrop-blur-md p-4 shadow-2xl shadow-black/40"
              style={popStyle}
              data-testid="tour-popover"
              role="dialog"
              aria-label={step.title}
            >
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary" data-testid="tour-step-counter">
                Schritt {idx + 1} von {TOUR_STEPS.length}
              </div>
              <div className="font-display text-sm font-bold mt-1">
                {idx === 0 && tenant?.name ? `Willkommen bei CarOS, ${tenant.name}!` : step.title}
              </div>
              <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{step.body}</p>
              <div className="flex items-center justify-between gap-2 mt-3.5">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground"
                  onClick={skip}
                  data-testid="tour-skip-button"
                >
                  Überspringen
                </Button>
                <div className="flex items-center gap-1.5">
                  {idx > 0 && (
                    <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={prev} data-testid="tour-back-button">
                      <ChevronLeft className="h-3.5 w-3.5" /> Zurück
                    </Button>
                  )}
                  <Button size="sm" className="h-8 text-xs gap-1 font-semibold" onClick={next} data-testid="tour-next-button">
                    {isLast ? (
                      <>
                        <CheckCircle2 className="h-3.5 w-3.5" /> Fertig
                      </>
                    ) : (
                      <>
                        Weiter <ChevronRight className="h-3.5 w-3.5" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>,
        document.body
      )
    : null;

  return (
    <TourContext.Provider value={{ active, startTour, stopTour: skip }}>
      {children}
      {overlay}
    </TourContext.Provider>
  );
};

export default TourProvider;
